import React from 'react'
import { Waypoint } from 'react-waypoint'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import {
  CoveredWhyShouldYouCareClick,
  ScrolledIntoView,
  EVENTS_LABELS
} from 'src/constants/trackingEvents'
import { Button, Panel, Format, List, Text } from 'src/Components'
import HomeInsuranceDetail from 'src/Components/homeowners/HomeInsuranceDetail'
import { oneTimeTagEventDispatcher } from 'src/helpers/tagManager'

import styles from './HomeInsurancePanel.module.scss'

const scrollIntoView = oneTimeTagEventDispatcher(ScrolledIntoView, {
  eventLabel: EVENTS_LABELS.HomeInsuranceModule
})

const HomeInsurancePanel = ({
  daysUntilRenewal,
  yearsSincePurchase,
  estimatedValue,
  collapsed,
  showDetail
}) => (
  <Panel
    data-qa='HomeInsuranceCard'
    className={classnames(styles.card, collapsed && styles.showDetail)}
  >
    <Waypoint onEnter={scrollIntoView} bottomOffset='50%'>
      <div />
    </Waypoint>

    <div
      className={classnames(styles.container, collapsed && styles.collapsed)}
    >
      <Panel.Heading className={styles.heading}>
        <FormattedMessage
          id='Homeowner.homeInsurance.heading'
          defaultMessage="Your homeowner's insurance might renew in"
        />
      </Panel.Heading>

      <Text size='xxl' color='pink' weight='light' align='center' element='div'>
        <FormattedMessage
          id='Homeowner.homeInsurance.renewalDays'
          defaultMessage='{daysUntilRenewal} days'
          values={{ daysUntilRenewal }}
        />
      </Text>

      <Text
        className={styles.subheading}
        size='s'
        color='quiet'
        align='center'
        element='div'
      >
        <FormattedMessage
          id='Homeowner.homeInsurance.subheading'
          defaultMessage="assuming you're auto-renewing on your purchase date"
        />
      </Text>

      <div className={styles.explainerContainer}>
        <Panel className={styles.explainer}>
          <Text
            className={styles.explainerHeading}
            size='l'
            weight='light'
            align='center'
            element='div'
          >
            <FormattedMessage
              id='Homeowner.homeInsurance.yearsSincePurchase'
              defaultMessage="You've been in your home for {years}"
              values={{
                years: (
                  <Text color='pink' weight='light' element='span'>
                    <FormattedMessage
                      id='Homeowner.homeInsurance.yearsSincePurchaseInner'
                      defaultMessage='{yearsSincePurchase} years'
                      values={{ yearsSincePurchase }}
                    />
                  </Text>
                )
              }}
            />
          </Text>
          <List.ProConList>
            <List.ProConList.Con>
              <FormattedMessage
                id='Homeowner.homeInsurance.renewalPriceExplainer'
                defaultMessage='Prices often increase each year upon policy renewal'
              />
            </List.ProConList.Con>
            <List.ProConList.Pro>
              <FormattedMessage
                id='Homeowner.homeInsurance.renewalPriceCTA'
                defaultMessage='Find a cheaper policy and put the savings towards principal'
              />
            </List.ProConList.Pro>
          </List.ProConList>
        </Panel>
        <Panel className={styles.explainer}>
          <Text
            className={styles.explainerHeading}
            size='l'
            weight='light'
            align='center'
            element='div'
          >
            <FormattedMessage
              id='Homeowner.homeInsurance.estimatedValue'
              defaultMessage='Your estimated home value is {estimatedValue}'
              values={{
                estimatedValue: (
                  <Text color='pink' weight='light' element='span'>
                    <Format.Currency abbreviate value={estimatedValue} />
                  </Text>
                )
              }}
            />
          </Text>
          <List.ProConList>
            <List.ProConList.Info>
              <FormattedMessage
                id='Homeowner.homeInsurance.underinsuredExplainer'
                defaultMessage='You might be slightly underinsured - 60% of people are!'
              />
            </List.ProConList.Info>
            <List.ProConList.Info>
              <FormattedMessage
                id='Homeowner.homeInsurance.underinsuredCTA'
                defaultMessage='Make sure any new upgrades are covered'
              />
            </List.ProConList.Info>
          </List.ProConList>
        </Panel>
      </div>

      <Button
        className={styles.button}
        onClick={showDetail}
        track={CoveredWhyShouldYouCareClick}
        trackOptions={{ eventLabel: 'HomeInsurance' }}
      >
        <FormattedMessage
          id='Homeowner.homeInsurance.CTAButton'
          defaultMessage='Now what?'
        />
      </Button>
    </div>
    <div className={classnames(styles.detail, collapsed && styles.show)}>
      <HomeInsuranceDetail />
    </div>
  </Panel>
)

export default HomeInsurancePanel
